import axios from 'axios';
import { getAuthToken, getValidTenantId } from '../utils/authUtils';
import { API_BASE_URL } from '../config/constants';

// Create safe API request helper with better error handling
const apiRequest = async (method, url, data = null, config = {}) => {
  try {
    // Get authentication token
    const token = getAuthToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Get tenant ID
    const tenantId = getValidTenantId();
    if (!tenantId) {
      throw new Error('No valid tenant ID available');
    }

    // Create headers with auth token and tenant ID
    const headers = {
      'Authorization': `Bearer ${token}`,
      'X-Tenant-ID': tenantId,
      ...config.headers
    };

    // Build URL - ensure no path duplication
    const apiUrl = `${API_BASE_URL}/messages${url}`;
    console.log(`Making API request to: ${apiUrl}`);

    // Make the API request with proper error handling
    const response = await axios({
      method,
      url: apiUrl,
      data,
      headers,
      ...config
    });

    // Handle successful response
    return {
      success: true,
      data: response.data.data || response.data,
      totalPages: response.data.totalPages,
      currentPage: response.data.currentPage,
      total: response.data.total
    };
  } catch (error) {
    console.error('Message API error:', error);
    
    // Update the error handling to provide role-specific suggestions
    if (error.response?.status === 403) {
      console.error('Permission denied. This may be a role-based access issue.');
      
      // Try to help identify if user is trying to access admin endpoints as staff
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user.role === 'staff' && url.startsWith('/admin')) {
        console.warn('Staff users now have access to admin message endpoints. If you still see this error, ensure backend permissions are properly updated.');
      }
    }
    
    // Include request details in error for better debugging
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'An error occurred',
      status: error.response?.status || 500
    };
  }
};

// Message service functions
const messageService = {
  // Get all messages for admin or staff
  getAdminMessages: async (page = 1, limit = 10, filter = 'all') => {
    // Check if user is staff or admin to log appropriate information
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    console.log(`Fetching admin messages as ${user.role || 'unknown'} role`);
    
    return apiRequest('get', `/admin?page=${page}&limit=${limit}&filter=${filter}`);
  },

  // Get messages for a member
  getMemberMessages: async (page = 1, limit = 10, filter = 'all') => {
    return apiRequest('get', `/member?page=${page}&limit=${limit}&filter=${filter}`);
  },

  // Get message statistics for admin
  getMessageStats: async () => {
    return apiRequest('get', '/stats');
  },
  
  // Get message statistics for member
  getMemberMessageStats: async () => {
    return apiRequest('get', '/member/stats');
  },
  
  // Get unread message count for notification badge
  getUnreadCount: async () => {
    return apiRequest('get', '/unread-count');
  },

  // Get a single message by ID
  getMessageById: async (messageId) => {
    return apiRequest('get', `/${messageId}`);
  },

  // Send a new message (admin only)
  sendMessage: async (messageData) => {
    return apiRequest('post', '/', messageData);
  },
  
  // Send a message to multiple selected members (admin only)
  sendMessageToSelectedMembers: async (messageData) => {
    return apiRequest('post', '/to-selected-members', messageData);
  },

  // Send message from member to admin
  sendMessageToAdmin: async (messageData) => {
    return apiRequest('post', '/to-admin', messageData);
  },

  // Reply to a message
  replyToMessage: async (messageId, text) => {
    return apiRequest('post', `/${messageId}/reply`, { text });
  },

  // Mark a message as read
  markMessageAsRead: async (messageId) => {
    return apiRequest('patch', `/${messageId}/read`);
  },
  
  // Mark all messages as read
  markAllAsRead: async () => {
    return apiRequest('patch', '/mark-all-read');
  },

  // Delete a message (admin only)
  deleteMessage: async (messageId) => {
    return apiRequest('delete', `/${messageId}`);
  },

  // Get sent messages for a member
  getMemberSentMessages: async (page = 1, limit = 10) => {
    return apiRequest('get', `/member/sent?page=${page}&limit=${limit}`);
  },

  // Get sent messages for admin
  getAdminSentMessages: async (page = 1, limit = 10) => {
    return apiRequest('get', `/admin/sent?page=${page}&limit=${limit}`);
  },

  // Delete message for member (sent messages)
  memberDeleteMessage: async (messageId) => {
    return apiRequest('delete', `/member/${messageId}`);
  }
};

export default messageService;